import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import StarIcon from '@mui/icons-material/Star';
import VerifiedIcon from '@mui/icons-material/Verified';
import BusinessIcon from '@mui/icons-material/Business';
import { useVenueByPath } from '../hooks/useVenues';
import { ImageCarousel } from '../components/ImageCarousel';
import { ImageLightbox } from '../components/ImageLightbox';
import { BookingWidget } from '../components/BookingWidget';
import { VenueFoodPreview } from '../components/VenueFoodPreview';
import { AmenitiesGrid } from '../components/AmenitiesGrid';
import { OperatingHoursDisplay } from '../components/OperatingHoursDisplay';
import { MobileBookingBar } from '../components/MobileBookingBar';
import { ReviewsSection } from '../components/ReviewsSection';
import { WriteReviewModal } from '../components/WriteReviewModal';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import { Button } from '../../../components/ui/Button';
import { VenueInventoryDisplay } from '../../inventory/components/VenueInventoryDisplay';
import { VenueSEO } from '../../../components/seo/VenueSEO';
import { Helmet } from 'react-helmet-async';
import { VenueInstagramSection } from '../components/VenueInstagramSection';
import { MainLayout } from '../../../components/layouts/MainLayout';
import { ShareButton } from '../../../components/ui/ShareButton';
import { ContactVenueDialog } from '../../../components/ui/ContactVenueDialog';
import { useRecentlyViewed, formatVenueForRecentlyViewed } from '../../../hooks/useRecentlyViewed';
import { useVenueTypes, getVenueTypeLabel, getVenueTypeColor } from '../hooks/useVenueTypes';
import { useAuth } from '../../../contexts/AuthContext';
import { useCity } from '../../../contexts/CityContext';
import { useFeatureFlag } from '../../feature-flags/hooks/useFeatureFlags';
import { FEATURE_FLAGS } from '@lov/shared';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { VenueDetailTheme, VenueTypeIcon } from '../components/VenueDetailTheme';
import {
  formatPolicySummary,
  getPolicyRules,
  isPolicyConfigured,
} from '../../../lib/utils/cancellation-policy';
import type { VenueTypeCode } from '../../landing/constants/venueTypes';
import type { OperatingHours } from '@lov/shared';

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  position: 'relative' as const,
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
}));

const CarouselWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  [theme.breakpoints.up('md')]: {
    padding: '24px 24px 0',
    maxWidth: 1200,
    margin: '0 auto',
  },
}));

const BackButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 16,
  left: 16,
  zIndex: 10,
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(4px)',
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
  },
  [theme.breakpoints.up('md')]: {
    top: 40,
    left: 40,
  },
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  padding: '24px 16px',
  paddingBottom: 100, // Space for mobile booking bar
  [theme.breakpoints.up('md')]: {
    padding: '32px 24px',
    paddingBottom: 32,
  },
}));

const MainGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: 32,
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: '1fr 340px',
  },
}));

const MainContent = styled(Box)({
  minWidth: 0,
});

const SidebarContent = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('lg')]: {
    display: 'block',
  },
}));

// Header Section
const HeaderSection = styled(Box)({
  marginBottom: 24,
});

const BadgeRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  marginBottom: 12,
});

const VenueTypeBadge = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'bgColor' && prop !== 'textColor',
})<{ bgColor: string; textColor: string }>(({ bgColor, textColor }) => ({
  backgroundColor: bgColor,
  color: textColor,
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.8125rem',
  fontWeight: 500,
  borderRadius: 20,
  height: 28,
}));

const VerifiedBadge = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.success.main, 0.1),
  color: theme.palette.success.main,
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.8125rem',
  fontWeight: 500,
  borderRadius: 20,
  height: 28,
  '& .MuiChip-icon': {
    color: 'inherit',
    fontSize: 16,
  },
}));

const VenueName = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '1.75rem',
  fontWeight: 700,
  color: theme.palette.text.primary,
  marginBottom: 8,
  [theme.breakpoints.up('md')]: {
    fontSize: '2.25rem',
  },
}));

const RatingRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 4,
});

const RatingValue = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '1rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const ReviewCount = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

// Location Section
const LocationRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  color: theme.palette.text.secondary,
  marginTop: 8,
}));

const LocationText = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.9375rem',
  color: theme.palette.text.secondary,
  lineHeight: 1.5,
}));

// Section Components
const Section = styled(Box)(({ theme }) => ({
  marginBottom: 32,
  paddingBottom: 32,
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-of-type': {
    borderBottom: 'none',
    marginBottom: 0,
    paddingBottom: 0,
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '1.125rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: 16,
}));

const Description = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '1rem',
  color: theme.palette.text.secondary,
  lineHeight: 1.7,
  whiteSpace: 'pre-line',
}));

// Vendor Section
const VendorCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  padding: 16,
  backgroundColor: alpha(theme.palette.success.main, 0.04),
  borderRadius: 12,
  border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
}));

const VendorAvatar = styled(Box)(({ theme }) => ({
  width: 52,
  height: 52,
  borderRadius: 12,
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.success.main,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
}));

const VendorName = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '1rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const VendorVerified = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.8125rem',
  color: theme.palette.success.main,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
}));

// Error/Loading States
const CenteredContainer = styled(Box)(({ theme }) => ({
  minHeight: '60vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.background.default,
}));

const ErrorContent = styled(Box)({
  textAlign: 'center',
  padding: 24,
});

const ErrorTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '1.5rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: 8,
}));

const ErrorDescription = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '1rem',
  color: theme.palette.text.secondary,
  marginBottom: 24,
}));

export function VenueDetailPage() {
  const { stateSlug, citySlug, venueSlug } = useParams<{
    stateSlug: string;
    citySlug: string;
    venueSlug: string;
  }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [showMobileBar, setShowMobileBar] = useState(false);
  const [writeReviewOpen, setWriteReviewOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const bookingWidgetRef = useRef<HTMLDivElement>(null);

  const {
    data: venue,
    isLoading,
    error,
  } = useVenueByPath(stateSlug || '', citySlug || '', venueSlug || '');

  const { data: venueTypes } = useVenueTypes();
  const { addToRecentlyViewed } = useRecentlyViewed();
  const { isAuthenticated } = useAuth();
  const { skipCitySelection, selectedCity } = useCity();
  const { data: competitiveFlag } = useFeatureFlag(FEATURE_FLAGS.COMPETITIVE_LEADERBOARD);
  const isCompetitiveVenue = ['SNOOKER', 'GAMING'].includes(venue?.venueType ?? '');

  // Suppress city-selection modal on direct venue URL navigation — city is implicit from the URL
  useEffect(() => {
    if (!selectedCity) {
      skipCitySelection();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track venue view for "Recently Viewed" feature
  useEffect(() => {
    if (venue) {
      addToRecentlyViewed(formatVenueForRecentlyViewed(venue));
    }
  }, [venue, addToRecentlyViewed]);

  // Handle scroll to show/hide mobile booking bar
  useEffect(() => {
    if (!isMobile || !bookingWidgetRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowMobileBar(!entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    observer.observe(bookingWidgetRef.current);
    return () => observer.disconnect();
  }, [isMobile, venue]);

  const handleImageClick = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleBookNow = () => {
    if (!venue) return;

    // Use slug-based booking URL
    const bookingUrl = `/${stateSlug}/${citySlug}/${venueSlug}/book`;

    // Check if user is authenticated
    if (!isAuthenticated) {
      // Redirect to login with return URL
      navigate('/login', { state: { from: bookingUrl } });
      return;
    }

    // Navigate to slug-based booking flow
    navigate(bookingUrl);
  };

  if (isLoading) {
    return (
      <MainLayout>
        <Helmet>
          <title>
            {venueSlug
              ? `${venueSlug.replace(/-/g, ' ')} | Lord of Venues`
              : 'Loading... | Lord of Venues'}
          </title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <CenteredContainer>
          <LoadingSpinner size="lg" />
        </CenteredContainer>
      </MainLayout>
    );
  }

  if (error || !venue) {
    return (
      <MainLayout>
        <CenteredContainer>
          <ErrorContent>
            <ErrorTitle>Venue not found</ErrorTitle>
            <ErrorDescription>
              The venue you're looking for doesn't exist or has been removed.
            </ErrorDescription>
            <Button variant="primary" onClick={() => navigate('/venues')}>
              Browse Venues
            </Button>
          </ErrorContent>
        </CenteredContainer>
      </MainLayout>
    );
  }

  // Build images array for carousel with fallback support
  // Replace broken via.placeholder.com URLs with picsum.photos fallbacks
  const images: string[] = [];

  // Helper to check if URL is from via.placeholder.com (known to be down)
  const isPlaceholderUrl = (url: string | null | undefined): boolean => {
    return !url || url.includes('via.placeholder.com');
  };

  // Add coverImage with fallback if needed
  const coverImageSrc = isPlaceholderUrl(venue.coverImage)
    ? `https://picsum.photos/seed/${venue.id}/800/400`
    : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      venue.coverImage!;
  images.push(coverImageSrc);

  // Add additional images, replacing broken placeholders
  if (venue.images && venue.images.length > 0) {
    venue.images.forEach((img, index) => {
      const imageSrc = isPlaceholderUrl(img)
        ? `https://picsum.photos/seed/${venue.id}-${index}/800/400`
        : img;

      if (!images.includes(imageSrc)) {
        images.push(imageSrc);
      }
    });
  }

  // Get active amenities
  const activeAmenities = Object.entries(venue.amenities || {})
    .filter(([, value]) => value === true)
    .map(([key]) => key);

  return (
    <MainLayout>
      <PageContainer>
        <VenueSEO venue={venue} />

        {/* Venue-type-specific background pattern overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            overflow: 'hidden',
            zIndex: 0,
          }}
        >
          <VenueDetailTheme venueType={venue.venueType as VenueTypeCode}>
            <span />
          </VenueDetailTheme>
        </Box>

        {/* Image Carousel */}
        <CarouselWrapper>
          <BackButton onClick={() => navigate('/venues')} aria-label="Back to venues">
            <ArrowBackIcon />
          </BackButton>
          <Box
            sx={{
              position: 'absolute',
              top: { xs: 16, md: 40 },
              right: { xs: 16, md: 40 },
              zIndex: 10,
            }}
          >
            <ShareButton
              title={venue.name}
              text={`Check out ${venue.name} - ${venue.city?.name}, ${venue.state?.name}`}
            />
          </Box>
          <ImageCarousel images={images} venueName={venue.name} onImageClick={handleImageClick} />
        </CarouselWrapper>

        {/* Image Lightbox */}
        <ImageLightbox
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          images={images}
          initialIndex={lightboxIndex}
          venueName={venue.name}
        />

        {/* Content */}
        <ContentContainer maxWidth="lg">
          <MainGrid>
            {/* Main Content */}
            <MainContent>
              {/* Header */}
              <HeaderSection>
                <BadgeRow>
                  <VenueTypeIcon venueType={venue.venueType} size={20} />
                  <VenueTypeBadge
                    bgColor={getVenueTypeColor(venue.venueType, venueTypes).bg}
                    textColor={getVenueTypeColor(venue.venueType, venueTypes).text}
                    label={getVenueTypeLabel(venue.venueType, venueTypes)}
                    size="small"
                  />
                  {venue.isApproved && (
                    <VerifiedBadge icon={<VerifiedIcon />} label="Verified" size="small" />
                  )}
                </BadgeRow>

                <VenueName variant="h1">{venue.name}</VenueName>

                {venue.averageRating != null && venue.averageRating > 0 && (
                  <RatingRow>
                    <StarIcon sx={{ fontSize: 20, color: '#FBBF24' }} />
                    <RatingValue>{venue.averageRating.toFixed(1)}</RatingValue>
                    <ReviewCount>
                      ({venue.totalReviews} review{venue.totalReviews !== 1 ? 's' : ''})
                    </ReviewCount>
                  </RatingRow>
                )}

                <LocationRow>
                  <LocationOnOutlinedIcon sx={{ fontSize: 20, mt: 0.25 }} />
                  <LocationText>
                    {venue.address}
                    {venue.locality && <>, {venue.locality}</>}
                    <br />
                    {venue.city?.name}, {venue.state?.name} - {venue.pincode}
                  </LocationText>
                </LocationRow>

                {/* Contact Button — only shown when vendor has made contact details public */}
                {(venue as Record<string, unknown>).isContactPublic === true &&
                  (venue.whatsappNumber || venue.email || venue.phoneNumber) && (
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setContactDialogOpen(true)}
                      >
                        Ask a Question
                      </Button>
                    </Box>
                  )}

                {/* Competitive Leaderboard — shown for SNOOKER/GAMING venues when flag is on */}
                {isAuthenticated && competitiveFlag?.enabled && isCompetitiveVenue && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/venues/${venue.id}/competitive`)}
                    >
                      <EmojiEventsIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      Competitive Profile
                    </Button>
                  </Box>
                )}
              </HeaderSection>

              {/* Mobile Booking Widget Reference */}
              {isMobile && (
                <Box ref={bookingWidgetRef} sx={{ mb: 3 }}>
                  <BookingWidget
                    venueId={venue.id}
                    basePrice={venue.basePrice}
                    currency={venue.currency}
                    maxCapacity={venue.maxCapacity ?? undefined}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    phone={(venue as any).isContactPublic ? venue.phoneNumber : undefined}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    whatsapp={(venue as any).isContactPublic ? venue.whatsappNumber : undefined}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    email={(venue as any).isContactPublic ? venue.email : undefined}
                    onBookNow={handleBookNow}
                  />
                </Box>
              )}

              {/* About */}
              <Section>
                <SectionTitle>About</SectionTitle>
                <Description>{venue.description}</Description>
              </Section>

              {/* Amenities */}
              {activeAmenities.length > 0 && (
                <Section>
                  <SectionTitle>Amenities</SectionTitle>
                  <AmenitiesGrid amenities={activeAmenities} />
                </Section>
              )}

              {/* Operating Hours */}
              {venue.operatingHours && Object.keys(venue.operatingHours).length > 0 && (
                <Section>
                  <SectionTitle>Operating Hours</SectionTitle>
                  <OperatingHoursDisplay
                    operatingHours={venue.operatingHours as OperatingHours}
                    variant="full"
                    highlightToday
                  />
                </Section>
              )}

              {/* Food & Drinks */}
              {venue.amenities?.foodAndBeverages && (
                <Section>
                  <SectionTitle>Food & Drinks</SectionTitle>
                  <VenueFoodPreview venueId={venue.id} />
                </Section>
              )}

              {/* Cancellation Policy */}
              <Section>
                <SectionTitle>Cancellation Policy</SectionTitle>
                {!isPolicyConfigured(venue.cancellationPolicy) && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1, fontFamily: '"IBM Plex Sans", sans-serif' }}
                  >
                    Standard cancellation policy applies.
                  </Typography>
                )}
                {formatPolicySummary(getPolicyRules(venue.cancellationPolicy)).map((line, i) => (
                  <Typography
                    key={i}
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      py: 0.75,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      fontFamily: '"IBM Plex Sans", sans-serif',
                    }}
                  >
                    {line}
                  </Typography>
                ))}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1.5, display: 'block', fontFamily: '"IBM Plex Sans", sans-serif' }}
                >
                  Refunds are processed to your wallet within 24-48 hours of cancellation.
                </Typography>
              </Section>

              {/* Equipment/Inventory */}
              <Section>
                <SectionTitle>Equipment & Stations</SectionTitle>
                <VenueInventoryDisplay venueId={venue.id} />
              </Section>

              {/* Vendor Info */}
              {venue.vendor && (
                <Section>
                  <SectionTitle>Managed by</SectionTitle>
                  <VendorCard>
                    <VendorAvatar>
                      <BusinessIcon sx={{ fontSize: 28 }} />
                    </VendorAvatar>
                    <Box>
                      <VendorName>{venue.vendor.businessName}</VendorName>
                      {venue.vendor.isVerified && (
                        <VendorVerified>
                          <VerifiedIcon sx={{ fontSize: 16 }} />
                          Verified Vendor
                        </VendorVerified>
                      )}
                    </Box>
                  </VendorCard>
                </Section>
              )}

              {/* Instagram Section */}
              {venue.vendor?.instagram && (
                <Section>
                  <VenueInstagramSection
                    instagramHandle={venue.vendor.instagram}
                    venueName={venue.name}
                  />
                </Section>
              )}

              {/* Reviews */}
              <Section>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <SectionTitle>Reviews</SectionTitle>
                  <Button variant="primary" onClick={() => setWriteReviewOpen(true)}>
                    Write a Review
                  </Button>
                </Box>
                <ReviewsSection
                  venueId={venue.id}
                  averageRating={venue.averageRating ?? undefined}
                  totalReviews={venue.totalReviews ?? undefined}
                />
              </Section>
            </MainContent>

            {/* Desktop Sidebar */}
            <SidebarContent>
              <BookingWidget
                venueId={venue.id}
                basePrice={venue.basePrice}
                currency={venue.currency}
                maxCapacity={venue.maxCapacity ?? undefined}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                phone={(venue as any).isContactPublic ? venue.phoneNumber : undefined}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                whatsapp={(venue as any).isContactPublic ? venue.whatsappNumber : undefined}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                email={(venue as any).isContactPublic ? venue.email : undefined}
                onBookNow={handleBookNow}
              />
            </SidebarContent>
          </MainGrid>
        </ContentContainer>

        {/* Write Review Modal */}
        {/* TODO: Pass actual bookingId from user's booking history */}
        <WriteReviewModal
          open={writeReviewOpen}
          onClose={() => setWriteReviewOpen(false)}
          bookingId={null}
          venueName={venue.name}
        />

        {/* Contact Venue Dialog */}
        <ContactVenueDialog
          open={contactDialogOpen}
          onClose={() => setContactDialogOpen(false)}
          venueName={venue.name}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          whatsapp={(venue as any).isContactPublic ? venue.whatsappNumber : undefined}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          email={(venue as any).isContactPublic ? venue.email : undefined}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          phone={(venue as any).isContactPublic ? venue.phoneNumber : undefined}
        />

        {/* Mobile Booking Bar */}
        {isMobile && (
          <MobileBookingBar
            basePrice={venue.basePrice}
            currency={venue.currency}
            onBookNow={handleBookNow}
            visible={showMobileBar}
          />
        )}
      </PageContainer>
    </MainLayout>
  );
}
